import React, { useCallback, useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { LuArrowLeft, LuShare2 } from 'react-icons/lu';
import Page from '../../components/ui/Page/Page';
import PageHeader from '../../components/ui/PageHeader/PageHeader';
import Card from '../../components/ui/Card/Card';
import Button from '../../components/ui/Button/Button';
import { SkeletonCard } from '../../components/ui/LoadingSkeleton/LoadingSkeleton';
import ErrorState from '../../components/ui/ErrorState/ErrorState';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import ReceiptSummary from '../../components/domain/ReceiptSummary/ReceiptSummary';
import { getUserSession } from '../../api/userSessionsApi';
import { adaptAccountSession } from '../../utils/accountSessionRecord';
import { offersCustomerReceipt } from '../../utils/billingDisplay';
import { isNative } from '../../platform/platform';
import { shareReceipt } from '../../platform/sharing';
import { useOptionalToast } from '../../context/ToastContext';
import ReceiptEmailCard from './components/ReceiptEmailCard';
import CanonicalSessionPanel from '../../components/domain/CanonicalSessionPanel/CanonicalSessionPanel';
import SessionTimeline from '../../components/domain/CanonicalSessionPanel/SessionTimeline';
import { getCanonicalSessionDetail, markCanonicalViewed } from '../../api/canonicalSessionApi';
import { useCanonicalSession } from '../../chargerUx/useCanonicalSession';
import { onResume } from '../../platform/lifecycle';

/**
 * Authenticated session detail — `/history/:sessionUid`.
 *
 * Data source is `GET /api/user/sessions/:sessionUid` behind
 * `authRequired`. Ownership is derived from the JWT — a request for
 * someone else's session comes back 404, indistinguishable from
 * "no such session" (deliberate on the backend). We surface both
 * as the same "we couldn't find this session" state.
 *
 * Deliberately NEVER reads from `localHistoryStore` — the history
 * list on this page is server-driven; the device-scoped store may
 * not have this row at all (e.g. a session started on another
 * device or before the recovery cookie was minted).
 */
function SessionDetailPage() {
  const { sessionUid } = useParams();
  const toast = useOptionalToast();
  const [session, setSession] = useState(null);
  // { notFound: boolean, message: string } — kept separate from
  // `session` so a retry doesn't wipe a previously-loaded row.
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  // Charger UX: canonical outcome + simplified timeline, when the API offers
  // them. This page is also where a notification lands, so the state is
  // always fetched here — never taken from what the notification said.
  const [timeline, setTimeline] = useState([]);
  const { snapshot: canonical } = useCanonicalSession(sessionUid);

  const refresh = useCallback(async () => {
    setError(null);
    setLoading(true);
    const [result, detail] = await Promise.all([
      getUserSession(sessionUid),
      getCanonicalSessionDetail(sessionUid),
    ]);
    if (detail.ok) {
      setTimeline(detail.timeline);
      // The client has now seen the authoritative state: notices clear.
      if (detail.canonical) markCanonicalViewed(sessionUid, detail.canonical.version);
    }
    if (result.ok) {
      // `adaptAccountSession` turns the backend DTO (nested camelCase
      // — `stationMeta.name`, `energy.kwh`, `cost.total`, ...) into
      // the flat shape `ReceiptSummary` reads.
      setSession(adaptAccountSession(result.session));
    } else {
      setError({
        notFound: !!result.notFound,
        message: result.message,
      });
    }
    setLoading(false);
  }, [sessionUid]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Returning to the app: what is on screen may be old.
  useEffect(() => {
    let off = null;
    let cancelled = false;
    Promise.resolve(onResume(() => { refresh(); })).then((unsubscribe) => {
      if (cancelled) { if (typeof unsubscribe === 'function') unsubscribe(); } else off = unsubscribe;
    }).catch(() => {});
    return () => { cancelled = true; if (typeof off === 'function') off(); };
  }, [refresh]);

  const showInitialSkeleton = loading && !session && !error;
  // A Test session has no customer receipt (the backend says so on
  // `billing.display.receipt`): neither receipt action is offered.
  const receiptOffered = !!session && offersCustomerReceipt(session);
  // Only render the receipt when we have real data. `loading` is
  // allowed to stay true during a retry — the receipt keeps
  // rendering underneath so the page doesn't flash empty.

  return (
    <Page>
      <PageHeader
        title="Session details"
        subtitle={session?.stationName || 'Charging session'}
        action={
          <Button as={Link} to="/history" variant="ghost" leadingIcon={<LuArrowLeft />}>
            Back
          </Button>
        }
      />

      {showInitialSkeleton && <SkeletonCard />}

      {!showInitialSkeleton && error && !session && (
        <Card>
          {error.notFound ? (
            <EmptyState
              title="We couldn't find this session"
              description="It may belong to a different account, or it may have been removed."
              action={
                <Button as={Link} to="/history" variant="secondary" leadingIcon={<LuArrowLeft />}>
                  Back to history
                </Button>
              }
            />
          ) : (
            <ErrorState
              title="Couldn't load your session"
              description={error.message || 'Please check your connection and try again.'}
              onRetry={refresh}
            />
          )}
        </Card>
      )}

      {session && canonical && (
        <CanonicalSessionPanel snapshot={canonical} compact />
      )}

      {session && <ReceiptSummary session={session} />}

      {session && canonical && timeline.length > 0 && (
        <Card>
          <SessionTimeline entries={timeline} />
        </Card>
      )}

      {receiptOffered && isNative() && (
        <Card>
          {/*
            Native share affordance. The OS share sheet takes care of
            "which app is used" — Mail, Messages, WhatsApp, print,
            copy link, whatever the user has installed. On web we do
            NOT render this button: the existing "Email me a receipt"
            flow already covers the equivalent journey.
          */}
          <Button
            variant="secondary"
            leadingIcon={<LuShare2 />}
            onClick={async () => {
              // Share the receipt from THIS app's own origin so the link matches the
              // environment (staging shares a staging link, prod a prod link) instead
              // of a hardcoded production host.
              const shareOrigin =
                (typeof window !== 'undefined' && window.location && window.location.origin) ||
                'https://greencharge.app';
              const url = `${shareOrigin}/history/${encodeURIComponent(sessionUid)}`;
              const res = await shareReceipt({
                sessionUid,
                url,
                energyKWh: session?.energyKwh,
                cost: session?.costTotal,
              });
              if (res.ok && res.clipboard) {
                toast.showToast({ message: 'Receipt link copied', type: 'success' });
              } else if (!res.ok && res.reason && res.reason !== 'cancelled') {
                toast.showToast({ message: 'Could not share receipt.', type: 'danger' });
              }
            }}
          >
            Share receipt
          </Button>
        </Card>
      )}

      {receiptOffered && <ReceiptEmailCard sessionUid={sessionUid} />}
    </Page>
  );
}

export default SessionDetailPage;
